import React, { useEffect } from 'react';
import { Snackbar } from '@mui/material/';
import { AppDispatch, IReduxState } from 'app/store';
import { useDispatch, useSelector } from 'react-redux';
import { hideAlert, resetDelay, resetTitle } from '../../../features/AlertSlice';
import { Common } from '../../services/common.service';
import { Amplitude } from 'common/services/amplitude.service';
import classNames from 'classnames';
import useLocalStorage from 'common/hooks/useLocalStorage';
import './Alert.scss';

const iconThumbsUp = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
    <path
      d="M4.99512 6.79681H1.99512C1.86251 6.79681 1.73533 6.84949 1.64156 6.94326C1.5478 7.03703 1.49512 7.16421 1.49512 7.29681V12.7968C1.49512 12.9294 1.5478 13.0566 1.64156 13.1504C1.73533 13.2441 1.86251 13.2968 1.99512 13.2968H4.99512M4.99512 6.79681V13.2968M4.99512 6.79681L7.49512 1.79681C8.02555 1.79681 8.53426 2.00753 8.90933 2.3826C9.2844 2.75767 9.49512 3.26638 9.49512 3.79681V5.29681H13.4951C13.637 5.29685 13.7772 5.32707 13.9065 5.38546C14.0357 5.44385 14.1511 5.52908 14.2449 5.63548C14.3387 5.74188 14.4088 5.86703 14.4506 6.0026C14.4923 6.13817 14.5047 6.28107 14.487 6.42181L13.737 12.4218C13.7066 12.6634 13.589 12.8856 13.4064 13.0467C13.2238 13.2078 12.9886 13.2967 12.7451 13.2968H4.99512"
      stroke="#5D6576"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

const iconThumbsDown = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
    <path
      d="M4.99512 3.29681H1.99512C1.86251 3.29681 1.73533 3.34949 1.64156 3.44326C1.5478 3.53703 1.49512 3.66421 1.49512 3.79681V9.29681C1.49512 9.42942 1.5478 9.5566 1.64156 9.65037C1.73533 9.74414 1.86251 9.79681 1.99512 9.79681H4.99512M4.99512 3.29681V9.79681M4.99512 3.29681H12.7451C12.9886 3.29688 13.2238 3.38581 13.4064 3.54691C13.589 3.70801 13.7066 3.9302 13.737 4.17181L14.487 10.1718C14.5047 10.3126 14.4923 10.4555 14.4506 10.591C14.4088 10.7266 14.3387 10.8517 14.2449 10.9581C14.1511 11.0645 14.0357 11.1498 13.9065 11.2082C13.7772 11.2666 13.637 11.2968 13.4951 11.2968H9.49512V12.7968C9.49512 13.3272 9.2844 13.836 8.90933 14.211C8.53426 14.5861 8.02555 14.7968 7.49512 14.7968L4.99512 9.79681"
      stroke="#5D6576"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

export function Alert() {
  const dispatch: AppDispatch = useDispatch();
  const { variant, title, text, visible, delay, isAiAlert } = useSelector((state: IReduxState) => state.alert);
  const { storedValue: storageSearch, removeItem: removeStorageSearch } = useLocalStorage('ai_search');

  const handleClose = () => {
    removeStorageSearch();
    dispatch(hideAlert());
    dispatch(resetDelay());
    dispatch(resetTitle());
  };

  useEffect(() => {
    if (visible) {
      Common.displayHelp(false);
    }
  }, [visible]);

  const handleThumb = (type: string) => () => {
    // console.log('XXX: ', { feedback: type, search: storageSearch, url: window.location.href });
    Amplitude.save('AI_SEARCH_THUMBS', { feedback: type, search: storageSearch, url: window.location.href });
    removeStorageSearch();
    dispatch(hideAlert());
  };

  return (
    <Snackbar
      open={visible}
      autoHideDuration={delay}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      onClose={handleClose}
    >
      <div className={classNames('Alert', { 'ai-alert': isAiAlert })}>
        <div style={isAiAlert ? { paddingTop: 18 } : {}}>
          <img src={`/assets/images/${variant}.svg`} className="icon" alt="Icon" />
        </div>
        <div style={isAiAlert ? { paddingTop: 18 } : {}}>
          <h6>{title || variant}</h6>
          <p>{text}</p>
        </div>
        {isAiAlert && (
          <>
            <div style={{ flexGrow: 1 }} />
            <div>
              <span
                className="thumb-down blink"
                onClick={handleThumb('down')}
                style={{ marginRight: 12, position: 'relative', top: 5 }}
              >
                {iconThumbsDown}
              </span>
              <span
                className="thumb-up blink"
                style={{ paddingRight: 28, position: 'relative', bottom: -2 }}
                onClick={handleThumb('up')}
              >
                {iconThumbsUp}
              </span>
            </div>
          </>
        )}
        <img
          style={isAiAlert ? { paddingTop: 3 } : {}}
          src="/assets/images/x.svg"
          className="close"
          onClick={handleClose}
          alt="Close"
        />
      </div>
    </Snackbar>
  );
}
